import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { H1 } from '../../../components/Typography';
import { HumanBoolean, Money } from '../../../components/Formatters';
import { Dl, Dt, Dd } from '../../../components/DescriptionList/Horizontal';
import { ButtonGroup, IconButton } from '../../../components/Buttons';
import { distances, grid, transaction_status } from '../../../styles/constants';
import { Session, Transaction } from '../../../types/checkout';
import { Allow } from '../../../auth';
import { useActions } from '../../../Actions';
import { namespace } from '../';

import {
    getCapturableAmount,
    getCurrentEvent,
    getRefundableAmount,
    hasCapturableState,
    hasRefundableState,
    getCapturedAmount,
    getRefundedAmount,
    hasRefundablePaymentProvider,
    hasVoidableState,
    hasSplitPayment,
    canExtendAuthorization
} from '../../transactionHelpers';
import Icon from '../../../components/Icons';
import { useAccountId } from '../../../auth/useAccountId';
import { getCurrentLanguage } from '../../../i18n';
import TransactionStatus from '../../components/TransactionStatus';

export interface HeroProps extends WithTranslation {
    transaction: Transaction;
    session?: Session;
    reportTransaction: (accountId: string, transaction: Transaction, language: string) => void;
}

const Hero = (
    {
        t,
        i18n,
        transaction,
        session,
        reportTransaction,
    }: HeroProps
) => {
    const { setView } = useActions(namespace);
    const accountId = useAccountId();
    const capturableAmount = getCapturableAmount(transaction);
    const refundableAmount = getRefundableAmount(transaction);
    const capturedAmount = getCapturedAmount(transaction);
    const refundedAmount = getRefundedAmount(transaction);
    const status = transaction.status || getCurrentEvent(transaction).transaction_status;
    const zeroTransaction = transaction.payment_product_type === 'dintero.zero';
    const canCapture =
        (capturableAmount > 0 || zeroTransaction) &&
        session &&
        hasCapturableState(transaction);
    const canRefund =
        (refundableAmount > 0 || zeroTransaction) &&
        session &&
        hasRefundableState(transaction) &&
        hasRefundablePaymentProvider(transaction);
    const canVoid = hasVoidableState(transaction);
    const isSplit = hasSplitPayment(transaction);

    return (
        <HeroWrapper status={status || ''}>
            <Inner>
                <Title>{transaction.merchant_reference}</Title>
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.id')}</Dt>
                    <Dd className="amount">{transaction.id}</Dd>
                </Dl>
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.status')}</Dt>
                    <Dd className="amount">
                        {status && (
                            <TransactionStatus transaction={transaction} />
                        )}
                    </Dd>
                </Dl>
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.settlement_status')}</Dt>
                    <Dd className="amount">{t(`payments.settlement_statuses.${transaction.settlement_status}`)}</Dd>
                </Dl>
                {isSplit && <Dl>
                    <Dt doubleColumn>{t('payments.fields.split_payment')}</Dt>
                    <Dd className="amount">
                        <HumanBoolean value={isSplit} />
                    </Dd>
                </Dl>}
                {transaction.payout_destination_id && <Dl>
                    <Dt doubleColumn>{t('payments.fields.payout_destination')}</Dt>
                    <Dd className="amount">
                        <code>{transaction.payout_destination_id}</code>
                    </Dd>
                </Dl>}
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.authorized_amount')}</Dt>
                    <Dd className="amount">
                        <Money amount={transaction.amount} currency={transaction.currency} />
                    </Dd>
                </Dl>
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.captured')}</Dt>
                    <Dd className="amount">
                        <Money amount={capturedAmount} currency={transaction.currency} />
                    </Dd>
                </Dl>
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.refunded')}</Dt>
                    <Dd className="amount">
                        <Money amount={refundedAmount} currency={transaction.currency} />
                    </Dd>
                </Dl>
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.total')}</Dt>
                    <Dd className="amount">
                        <Money amount={capturedAmount - refundedAmount} currency={transaction.currency} />
                    </Dd>
                </Dl>
                <Allow anyOfScopes={[['admin'], ['admin:checkout'], ['write:checkout']]}>
                    <ActionWrapper>
                        <ButtonGroup>
                            {canCapture && (
                                <IconButton
                                    onClick={() => setView('capture')}
                                    icon={<Icon icon="arrow_right_small" />}
                                >
                                    {t('payments.operations.capture')}
                                </IconButton>
                            )}
                            {canExtendAuthorization(transaction) && (
                                <IconButton
                                    onClick={() => setView('extend')}
                                    icon={<Icon icon="arrow_right_small" />}
                                    className="alt"
                                >
                                    {t('payments.operations.extend')}
                                </IconButton>
                            )}
                            {refundableAmount > 0 &&
                                session &&
                                hasRefundableState(transaction) &&
                                hasRefundablePaymentProvider(transaction) && (
                                <IconButton
                                    onClick={() => setView('refund')}
                                    className="alt"
                                    icon={<Icon icon="arrow_right_small" />}
                                >
                                    {t('payments.operations.refund')}
                                </IconButton>
                            )}
                            {canVoid && (
                                <IconButton
                                    onClick={() => setView('void')}
                                    className="alt"
                                    icon={<Icon icon="arrow_right_small" />}
                                >
                                    {t('payments.operations.void')}
                                </IconButton>
                            )}
                            <IconButton
                                onClick={() => {
                                    reportTransaction(accountId, transaction, getCurrentLanguage(i18n));
                                }}
                                className="alt"
                                icon={<Icon icon="pdf" />}
                            >
                                {t(`payments.operations.report`)}
                            </IconButton>
                        </ButtonGroup>
                    </ActionWrapper>
                </Allow>
            </Inner>
        </HeroWrapper>
    );
};

export default withTranslation()(Hero);
interface HeroWrapperProps {
    status: string;
}
const HeroWrapper = styled.div<HeroWrapperProps>`
    width: 100%;
    position: relative;
    border-top: 2px solid ${(props) => transaction_status[props.status].border || 'transparent'};
    z-index: 1;
`;


const Inner = styled.div`
    max-width: ${grid.spans.span8};
    margin: 0 auto;
    padding: ${distances.large} ${distances.small};

    @media (max-width: 768px) {
        padding: ${distances.small};
    }
`;

const ActionWrapper = styled.div`
    margin-top: ${distances.small};
`;

const Title = styled(H1)`
    word-break: break-all;
`;
